import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Alert, Button, DatePicker, Modal, Select } from 'antd';
import PropTypes from 'prop-types';
import { fetchMedications } from '../../../medications/api/medicationsApi';
import { updateTherapyTemplate } from '../../api/therapiesApi';
import fixedBottomDatePickerProps from '../../../../shared/utils/datePickerPlacement';
import {
  getTherapyPatientName,
  getTherapyTypeLabel,
} from '../../utils/therapyPresentation';

const durationOptions = ['1', '2', '3', '4', '5', '6'];
const updateTherapyNotice =
  'Ova akcija se koristi za izmenu centralnog datuma, u situaciji kada doktor naknadno izmeni centralni datum. Akcija ne služi za izmenu leka u toku terapije ili obnovu terapije.';
const takenStatuses = new Set(['taken']);
const warningIcon = (
  <ExclamationCircleFilled style={{ color: '#a8071a', fontSize: 18 }} />
);

const initialForm = {
  centralDate: '',
  durationMonths: '3',
  medications: [],
};

const normalizeCentralDate = (therapy) => {
  if (therapy?.centralDate)
    return dayjs(therapy.centralDate).format('YYYY-MM-DD');

  const firstOccurrence = Array.isArray(therapy?.occurrences)
    ? therapy.occurrences.find((occurrence) => occurrence?.scheduledDate)
    : null;

  return firstOccurrence?.scheduledDate
    ? dayjs(firstOccurrence.scheduledDate).format('YYYY-MM-DD')
    : '';
};

const getTherapyFromResponse = (data) =>
  data?.therapy || data?.data?.therapy || data?.data || null;

const getTakenOccurrenceNumbers = (occurrences) =>
  (Array.isArray(occurrences) ? occurrences : [])
    .filter((occurrence) => takenStatuses.has(occurrence?.status))
    .map((occurrence, index) => occurrence?.sequenceNumber || index + 1);

const UpdateTherapyTemplateModal = ({
  isOpen,
  onClose,
  therapy,
  occurrences = [],
  onUpdated,
}) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadMedications = async () => {
      setMedicationsLoading(true);

      const { data, ok, message } = await fetchMedications({
        page: 1,
        limit: 10000,
      });

      if (cancelled) return;

      if (!ok) {
        setMedicationOptions([]);
        setError(message || 'Doslo je do greske pri dobavljanju lekova.');
        setMedicationsLoading(false);
        return;
      }

      const options = Array.isArray(data?.medications)
        ? data.medications.map((medication) => ({
            value: medication.name,
            label: medication.name,
          }))
        : [];

      setMedicationOptions(options);
      setMedicationsLoading(false);
    };

    setForm({
      centralDate: normalizeCentralDate(therapy),
      durationMonths: String(
        therapy?.isAcute ? 1 : therapy?.durationMonths || 3
      ),
      medications: Array.isArray(therapy?.medications)
        ? therapy.medications
        : [],
    });
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setLoading(false);
    setIsConfirmOpen(false);
    setMedicationOptions([]);

    loadMedications();

    return () => {
      cancelled = true;
    };
  }, [isOpen, therapy]);

  const handleSelectMedication = (value) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.includes(value)
        ? prev.medications
        : [...prev.medications, value],
    }));
    setMedicationSearch('');
  };

  const handleRemoveMedication = (index) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const handleOk = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }

    const centralDate = form.centralDate?.trim();
    if (!centralDate) {
      setError('Popunite centralni datum.');
      return;
    }

    const payload = {
      centralDate: `${centralDate}T00:00:00.000Z`,
      durationMonths: Number(
        therapy?.isAcute ? '1' : form.durationMonths || '3'
      ),
      medications: form.medications || [],
    };

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const { data, ok, message, errorCode } = await updateTherapyTemplate(
      therapy._id,
      payload
    );

    if (!ok) {
      const nextError =
        errorCode === 'THERAPY_HISTORY_LOCKED'
          ? 'Terapija ima istoriju/cikluse i ne moze da se menja ovim azuriranjem.'
          : message;
      setError(nextError);
      setLoading(false);
      return;
    }

    const updatedTherapy = getTherapyFromResponse(data) || {
      _id: therapy._id,
      ...payload,
    };
    setSuccessMessage(message || 'Terapija azurirana.');
    setDone(true);
    await onUpdated?.(updatedTherapy);
    setTimeout(() => {
      setDone(false);
      onClose?.();
    }, 800);
    setLoading(false);
  }, [form, onClose, onUpdated, therapy?._id, therapy?.isAcute]);

  const handleCancel = () => {
    setForm(initialForm);
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setIsConfirmOpen(false);
    onClose?.();
  };

  const handleSaveClick = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  const handleConfirmOk = useCallback(async () => {
    setIsConfirmOpen(false);
    await handleOk();
  }, [handleOk]);

  const availableMedicationOptions = useMemo(
    () =>
      medicationOptions.filter(
        (option) => !form.medications.includes(option.value)
      ),
    [form.medications, medicationOptions]
  );

  const takenOccurrenceNumbers = useMemo(
    () => getTakenOccurrenceNumbers(occurrences),
    [occurrences]
  );
  const hasTakenOccurrences = takenOccurrenceNumbers.length > 0;
  const takenOccurrenceLabel =
    takenOccurrenceNumbers.length === 1
      ? 'Mesečna aktivnost'
      : 'Mesečne aktivnosti';
  const takenOccurrenceVerb =
    takenOccurrenceNumbers.length === 1 ? 'je preuzeta' : 'su preuzete';

  return (
    <>
      <Modal
        className="create_user_modal"
        title="Izmeni aktuelnu terapiju"
        open={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button
            key="save"
            type="primary"
            onClick={handleSaveClick}
            disabled={loading || done}
            style={{ width: '150px' }}
          >
            {loading ? (
              <img
                src="/assets/icons/add.svg"
                alt="Učitavanje"
                className="animate-spin"
              />
            ) : done ? (
              <img
                src="/assets/icons/check.svg"
                alt="Done"
                className="check-icon"
              />
            ) : (
              'Sacuvaj'
            )}
          </Button>,
        ]}
      >
        <div className="create_user_modal_form" autoComplete="off">
          <Alert
            title={updateTherapyNotice}
            type="error"
            showIcon
            icon={warningIcon}
            style={{
              color: '#cf1322',
              borderColor: '#cf1322',
              borderWidth: 2,
              fontWeight: 600,
              marginBottom: 16,
            }}
          />
          {hasTakenOccurrences && (
            <Alert
              title={
                <div style={{ color: '#a8071a', fontWeight: 600 }}>
                  <p style={{ margin: 0 }}>
                    {takenOccurrenceLabel} {takenOccurrenceNumbers.join(', ')}{' '}
                    iz ove terapije {takenOccurrenceVerb}. To znači da je
                    terapija u toku. Ovom akcijom ćete narušiti podatke iz
                    trenutne terapije.
                  </p>
                  <p style={{ margin: '12px 0 0' }}>
                    Ako obnavljate terapiju, zatvorite ovaj prozor i iskoristite
                    dugme "Obnovi terapiju".
                  </p>
                  <p style={{ margin: '12px 0 0' }}>
                    Ako se lek promenio u toku terapije, prekinite terapiju i
                    iskoristite dugme "Prekini terapiju", a zatim dodajte novu.
                  </p>
                </div>
              }
              type="error"
              showIcon
              icon={warningIcon}
              style={{
                backgroundColor: '#fff1f0',
                borderColor: '#a8071a',
                borderWidth: 2,
                marginBottom: 16,
              }}
            />
          )}
          <div style={{ marginBottom: 12 }}>
            <strong>Pacijent:</strong> {getTherapyPatientName(therapy)}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Tip:</strong> {getTherapyTypeLabel(therapy)}
          </div>
          {therapy?.isAcute && (
            <Alert
              title="Akutna terapija traje 1 mesec."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="templateCentralDate"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Centralni datum
            </label>
            <DatePicker
              {...fixedBottomDatePickerProps}
              id="templateCentralDate"
              placeholder="dd/mm/yyyy"
              format="DD/MM/YYYY"
              style={{ width: '100%', height: 38 }}
              value={form.centralDate ? dayjs(form.centralDate) : null}
              onChange={(date) =>
                setForm((prev) => ({
                  ...prev,
                  centralDate: date ? date.format('YYYY-MM-DD') : '',
                }))
              }
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="templateDurationMonths"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Trajanje (meseci)
            </label>
            <select
              id="templateDurationMonths"
              name="durationMonths"
              value={form.durationMonths}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  durationMonths: event.target.value,
                }))
              }
              disabled={therapy?.isAcute}
              style={{
                width: '100%',
                height: 38,
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                padding: '0 10px',
              }}
            >
              {durationOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="templateTherapyMedicationSelect"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Lekovi
            </label>
            <Select
              id="templateTherapyMedicationSelect"
              showSearch
              value={null}
              searchValue={medicationSearch}
              placeholder={
                medicationsLoading
                  ? 'Ucitavanje lekova...'
                  : 'Izaberite lek iz sistema'
              }
              style={{ width: '100%' }}
              loading={medicationsLoading}
              disabled={
                medicationsLoading || availableMedicationOptions.length === 0
              }
              options={availableMedicationOptions}
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              notFoundContent={
                medicationsLoading
                  ? 'Ucitavanje lekova...'
                  : 'Nema dostupnih lekova'
              }
              onSearch={setMedicationSearch}
              onSelect={handleSelectMedication}
            />
            <div className="therapy-med-list">
              {form.medications.map((med, index) => (
                <div key={`${med}-${index}`} className="therapy-med-chip">
                  <span>{med}</span>
                  <button
                    type="button"
                    className="therapy-med-remove"
                    onClick={() => handleRemoveMedication(index)}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>
          {successMessage && (
            <Alert
              title={successMessage}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
          {error && (
            <Alert
              title={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Modal>
      <Modal
        title="Potvrda izmene"
        open={isConfirmOpen}
        onOk={handleConfirmOk}
        onCancel={() => setIsConfirmOpen(false)}
        okText="Da"
        cancelText="Ne"
        confirmLoading={loading}
      >
        <p>Da li ste sigurni da želite da uradite izmenu?</p>
      </Modal>
    </>
  );
};

UpdateTherapyTemplateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  occurrences: PropTypes.arrayOf(PropTypes.object),
  onUpdated: PropTypes.func,
};

export default UpdateTherapyTemplateModal;
